import type { ApiResponse } from '@/types'

/**
 * Normalize different API response shapes into the actual payload expected by callers.
 * Some backend handlers wrap data under additional `data` or `items` keys, and the
 * generic `apiRequest` helper also nests the original payload. This utility drills down
 * until it finds the underlying payload or returns `null` when it cannot.
 */
export function unwrapApiData<T>(
  response:
    | ApiResponse<T>
    | { data?: unknown; items?: unknown }
    | T
    | null
    | undefined
): T | null {
  if (response === null || response === undefined) {
    return null
  }

  const seen = new Set<unknown>()

  const unwrap = (value: any): any => {
    if (value === null || value === undefined) {
      return value
    }

    if (seen.has(value)) {
      return value
    }

    if (Array.isArray(value)) {
      return value
    }

    if (typeof value !== 'object') {
      return value
    }

    seen.add(value)

    if ('data' in value && value.data !== undefined) {
      const unwrapped = unwrap(value.data)
      if (unwrapped !== undefined) {
        return unwrapped
      }
    }

    if ('items' in value && Array.isArray(value.items)) {
      return value.items
    }

    return value
  }

  const result = unwrap(response)
  return (result ?? null) as T | null
}
